import type { Product } from "../product.js";
import { productRules, RULESET_VERSION } from "./rules.js";
import type { ProductRule, ValidationContext, ValidationIssue, ValidationResult } from "./types.js";

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 } as const;

/**
 * Datenqualität 0–100: jede verletzte Regel kostet je nach Schwere Punkte.
 * Ein blockierender Fehler wiegt deutlich schwerer als ein Hinweis – ein blockierter Artikel liegt so nie über 75 %.
 */
const PENALTY = { error: 25, warning: 8, info: 0 } as const;
function completeness(issues: ValidationIssue[]): number {
  const worstPerRule = new Map<string, number>();
  for (const i of issues) worstPerRule.set(i.ruleId, Math.max(worstPerRule.get(i.ruleId) ?? 0, PENALTY[i.severity]));
  return Math.max(0, 100 - [...worstPerRule.values()].reduce((a, b) => a + b, 0));
}

/**
 * Prüft einen Artikel gegen alle aktiven Regeln.
 * Ergebnis wird am Artikel gespeichert; `syncBlocked` verhindert die Übertragung ans Lager.
 */
export async function validateProduct(
  product: Product,
  ctx: ValidationContext,
  rules: ProductRule[] = productRules,
): Promise<ValidationResult> {
  const issues: ValidationIssue[] = [];

  for (const rule of rules) {
    const override = ctx.settings.ruleOverrides?.[rule.id];
    if (override?.enabled === false) continue;
    const found = await rule.check(product, ctx);
    for (const f of found) {
      issues.push({
        ruleId: rule.id,
        category: rule.category,
        field: f.field,
        message: f.message,
        ...(f.hint ? { hint: f.hint } : {}),
        // Mandanten-Override hat Vorrang vor regelinterner Eskalation
        severity: override?.severity ?? f.severity ?? rule.defaultSeverity,
      });
    }
  }

  issues.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  const hasError = issues.some((i) => i.severity === "error");
  const hasWarning = issues.some((i) => i.severity === "warning");

  return {
    status: hasError ? "invalid" : hasWarning ? "warnings" : "valid",
    syncBlocked: hasError,
    issues,
    completenessScore: completeness(issues),
    rulesetVersion: RULESET_VERSION,
    checkedAt: new Date().toISOString(),
  };
}
